/* eslint camelcase: 0 */
import _ from 'lodash';
import {
    ListingSnapshot,
    ListingsPage,
    ListingsFetchParams,
    ListingNumericFilter,
} from '../models';
import api, { rawClient } from './api';
import { getBackVersion } from '../config';

const toBrl = (x: number) => Math.trunc(x / 100);

const numericSerializationMap: Record<
    keyof ListingNumericFilter,
    [string[], (x: number) => number]
> = {
    maxPrice: [['rent_price_le', 'sell_price_le'], toBrl],
    maxBathrooms: [['bathrooms_le'], _.identity],
    maxBedrooms: [['bedrooms_le'], _.identity],
    maxSize: [['property_size_le'], _.identity],
    minPrice: [['rent_price_ge', 'sell_price_ge'], toBrl],
    minBathrooms: [['bathrooms_ge'], _.identity],
    minBedrooms: [['bedrooms_ge'], _.identity],
    minSize: [['property_size_ge'], _.identity],
};

const serializeNumericFilters = (filters: ListingNumericFilter) => {
    return Object.keys(numericSerializationMap)
        .filter((x: string): x is keyof ListingNumericFilter => Boolean(x))
        .filter(key => filters[key])
        .flatMap(key => {
            const [tags, converter] = numericSerializationMap[key];
            // eslint-disable-next-line
            const value = converter(filters[key]!);
            return tags.map(tag => `${tag}:${value}`);
        });
};

const enumFilters = {
    adTypes: (x: ListingsFetchParams['adTypes']) =>
        x && x.length === 1 ? [`ad_type:${x}`] : [],
    propertyTypes: (x: ListingsFetchParams['propertyTypes']) =>
        x && x.length ? [`property_type:${x.join('|')}`] : [],
};

const serializeFilterParams = ({
    propertyTypes = [],
    adTypes = [],
    ownersOnly = false,
    agentsOnly = false,
    favoritesOnly = false,
    pins = [],
    page,
    ...numeric
}: ListingsFetchParams) => {
    const filters = [
        enumFilters.adTypes(adTypes),
        enumFilters.propertyTypes(propertyTypes),
        ...serializeNumericFilters(numeric),
    ]
        .flatMap(_.identity)
        .join(',');
    return {
        filters: filters || undefined,
        owners_only: ownersOnly,
        agents_only: agentsOnly,
        favorites_only: favoritesOnly,
        pins: pins.join(',') || undefined,
        page,
    };
};

const getListings = (params: ListingsFetchParams): Promise<ListingsPage> => {
    return api.request<ListingsPage>({
        method: 'GET',
        url: `${getBackVersion()}/listings`,
        params: serializeFilterParams(params),
    });
};

const getListing = (listingId: string): Promise<ListingSnapshot> => {
    return api.request<ListingSnapshot>({
        method: 'GET',
        url: `${getBackVersion()}/listings/` + listingId,
    });
};

const downloadPictures = (listingId: string): Promise<void> => {
    return rawClient.download({
        action: 'LISTING_PICTURES_DOWNLOAD',
        method: 'GET',
        url: `${getBackVersion()}/listings/${listingId}/pictures`,
    });
};

const ListingResource = {
    getListing,
    getListings,
    downloadPictures,
};

export default ListingResource;
